import { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { FileDown, Loader2 } from 'lucide-react';
import { toast } from 'sonner';
import { Button } from '@/components/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Label } from '@/components/ui/label';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { ToggleGroup, ToggleGroupItem } from '@/components/ui/toggle-group';
import { DatePicker } from '@/components/shared/DatePicker';
import { useSettlementApi } from '@/services/api';
import { downloadBlob } from '@/utils/download';
import { isAfterWarsawToday, warsawToday } from '@/utils/warsawDate';
import { getLanguage, t } from '@/i18n';
import { salesStatementFileName } from '@ip/shared-types';
import type {
  SalesStatementCustomer,
  SalesStatementRequest,
  StatementLang,
  StatementType,
} from '@ip/shared-types';

export interface StatementExportDialogProps {
  companyNip: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Opened from the contractor card: the customer is fixed and the picker is hidden. */
  contractor?: { id: string; name: string };
}

type RangeMode = 'ytd' | 'custom';

interface StatementForm {
  customerKey: string;
  type: StatementType;
  lang: StatementLang;
  rangeMode: RangeMode;
  /** 1 January through today (Warsaw), fixed when the dialog opens. */
  ytd: { from: Date; to: Date };
  from: Date | null;
  to: Date | null;
  includePaid: boolean;
}

const ymd = (date: Date) => dayjs(date).format('YYYY-MM-DD');

function defaultLang(): StatementLang {
  const ui = getLanguage();
  return ui === 'en' || ui === 'pl' ? ui : 'pl';
}

/** Form defaults — used for the first render and again on every open. */
function initialForm(): StatementForm {
  const to = warsawToday();
  const from = dayjs(to).startOf('year').toDate();
  return {
    customerKey: '',
    type: 'statement',
    lang: defaultLang(),
    rangeMode: 'ytd',
    ytd: { from, to },
    from,
    to,
    includePaid: false,
  };
}

/**
 * Customer statement PDF (wyciąg dla klienta / potwierdzenie salda), PL or EN.
 * Every balance on the document is computed as of the range END date — the
 * backend ignores payments dated later — so the range can only end today or
 * earlier. The file name comes from the shared `salesStatementFileName`, the
 * same helper the backend uses for Content-Disposition (which the browser
 * save path does not read).
 */
export function StatementExportDialog({
  companyNip,
  open,
  onOpenChange,
  contractor,
}: StatementExportDialogProps) {
  const [form, setForm] = useState<StatementForm>(initialForm);
  const { customerKey, type, lang, rangeMode, from, to, includePaid } = form;
  const update = (patch: Partial<StatementForm>) => setForm((prev) => ({ ...prev, ...patch }));
  const [customers, setCustomers] = useState<SalesStatementCustomer[]>([]);
  const [loadingCustomers, setLoadingCustomers] = useState(false);
  const [downloading, setDownloading] = useState(false);

  const { getStatementCustomers, downloadSalesStatement } = useSettlementApi();
  const contractorId = contractor?.id;

  // Reset the form (and reload the customer list) each time the dialog opens.
  // A list that arrives after the dialog closed, reopened or switched company
  // is discarded (`cancelled` is set by the cleanup of that run).
  useEffect(() => {
    if (!open) return;
    setForm(initialForm());
    setCustomers([]);
    setLoadingCustomers(!contractorId);
    if (contractorId) return;
    let cancelled = false;
    getStatementCustomers(companyNip)
      .then((list) => {
        if (!cancelled) setCustomers(list);
      })
      .catch((error) => {
        if (cancelled) return;
        toast.error(error instanceof Error ? error.message : t('sales.statement.loadFailed'));
        setCustomers([]);
      })
      .finally(() => {
        if (!cancelled) setLoadingCustomers(false);
      });
    return () => {
      cancelled = true;
    };
  }, [open, companyNip, contractorId, getStatementCustomers]);

  const selectedCustomer = customers.find((c) => c.key === customerKey);

  const rangeFrom = rangeMode === 'ytd' ? form.ytd.from : from;
  const rangeTo = rangeMode === 'ytd' ? form.ytd.to : to;
  const rangeComplete =
    rangeFrom !== null && rangeTo !== null && !dayjs(rangeFrom).isAfter(rangeTo, 'day');
  const hasCustomer = contractor !== undefined || customerKey !== '';
  const canSubmit = !downloading && hasCustomer && rangeComplete;

  const handleDownload = async () => {
    if (!rangeFrom || !rangeTo || !rangeComplete) return;
    if (!contractor && !selectedCustomer) return;
    const fromYmd = ymd(rangeFrom);
    const toYmd = ymd(rangeTo);
    const body: SalesStatementRequest = {
      ...(contractor ? { contractorId: contractor.id } : { customerKey }),
      type,
      lang,
      from: fromYmd,
      to: toYmd,
      includePaid,
    };
    const customerName = contractor?.name ?? selectedCustomer?.name ?? '';
    try {
      setDownloading(true);
      const blob = await downloadSalesStatement(companyNip, body);
      downloadBlob(blob, salesStatementFileName(type, lang, customerName, toYmd));
      toast.success(t('sales.statement.downloaded'));
      onOpenChange(false);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : t('common.downloadFailed'));
    } finally {
      setDownloading(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-h-[85vh] overflow-y-auto sm:max-w-lg">
        <DialogHeader>
          <DialogTitle>{t('sales.statement.title')}</DialogTitle>
          <DialogDescription>
            {contractor
              ? t('sales.statement.descriptionContractor', { name: contractor.name })
              : t('sales.statement.description')}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          {!contractor && (
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">{t('sales.statement.customer')}</Label>
              <Select value={customerKey} onValueChange={(v) => update({ customerKey: v })} disabled={loadingCustomers}>
                <SelectTrigger className="w-full">
                  <SelectValue
                    placeholder={
                      loadingCustomers
                        ? t('common.loading')
                        : customers.length === 0
                          ? t('sales.statement.noCustomers')
                          : t('sales.statement.customerPlaceholder')
                    }
                  >
                    {selectedCustomer
                      ? `${selectedCustomer.name}${selectedCustomer.nip ? ` · NIP: ${selectedCustomer.nip}` : ''}`
                      : undefined}
                  </SelectValue>
                </SelectTrigger>
                <SelectContent>
                  {customers.map((c) => (
                    <SelectItem key={c.key} value={c.key}>
                      <div className="flex min-w-0 flex-col">
                        <span>
                          {c.name || '—'}
                          {c.nip && <span className="text-muted-foreground"> · NIP: {c.nip}</span>}
                        </span>
                        {c.address && (
                          <span className="text-muted-foreground text-xs">{c.address}</span>
                        )}
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          )}

          <div className="grid gap-4 sm:grid-cols-2">
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">{t('sales.statement.type')}</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={type}
                onValueChange={(v) => v && update({ type: v as StatementType })}
                aria-label={t('sales.statement.type')}
              >
                <ToggleGroupItem value="statement" className="px-3">
                  {t('sales.statement.typeStatement')}
                </ToggleGroupItem>
                <ToggleGroupItem value="balance_confirmation" className="px-3">
                  {t('sales.statement.typeBalanceConfirmation')}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
            <div className="space-y-1">
              <Label className="text-muted-foreground text-xs">{t('sales.statement.lang')}</Label>
              <ToggleGroup
                type="single"
                variant="outline"
                size="sm"
                value={lang}
                onValueChange={(v) => v && update({ lang: v as StatementLang })}
                aria-label={t('sales.statement.lang')}
              >
                <ToggleGroupItem value="pl" className="px-3">
                  {t('sales.statement.langPl')}
                </ToggleGroupItem>
                <ToggleGroupItem value="en" className="px-3">
                  {t('sales.statement.langEn')}
                </ToggleGroupItem>
              </ToggleGroup>
            </div>
          </div>

          <div className="space-y-2">
            <Label className="text-muted-foreground text-xs">{t('sales.statement.range')}</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={rangeMode}
              onValueChange={(v) => v && update({ rangeMode: v as RangeMode })}
              aria-label={t('sales.statement.range')}
            >
              <ToggleGroupItem value="ytd" className="px-3">
                {t('sales.statement.rangeYtd')}
              </ToggleGroupItem>
              <ToggleGroupItem value="custom" className="px-3">
                {t('sales.statement.rangeCustom')}
              </ToggleGroupItem>
            </ToggleGroup>
            {rangeMode === 'ytd' ? (
              <p className="text-sm tabular-nums">
                {ymd(form.ytd.from)} – {ymd(form.ytd.to)}
              </p>
            ) : (
              <div className="grid grid-cols-2 gap-3">
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">{t('sales.statement.from')}</Label>
                  <DatePicker
                    value={from}
                    onChange={(d) => update({ from: d })}
                    disabledDate={(d) => isAfterWarsawToday(d) || (to !== null && dayjs(d).isAfter(to, 'day'))}
                  />
                </div>
                <div className="space-y-1">
                  <Label className="text-muted-foreground text-xs">{t('sales.statement.to')}</Label>
                  <DatePicker value={to} onChange={(d) => update({ to: d })} disabledDate={isAfterWarsawToday} />
                </div>
              </div>
            )}
            <p className="text-muted-foreground text-xs">{t('sales.statement.asOfHint')}</p>
          </div>

          <div className="space-y-1">
            <Label className="text-muted-foreground text-xs">{t('sales.statement.scope')}</Label>
            <ToggleGroup
              type="single"
              variant="outline"
              size="sm"
              value={includePaid ? 'all' : 'open'}
              onValueChange={(v) => v && update({ includePaid: v === 'all' })}
              aria-label={t('sales.statement.scope')}
            >
              <ToggleGroupItem value="open" className="px-3">
                {t('sales.statement.scopeOpen')}
              </ToggleGroupItem>
              <ToggleGroupItem value="all" className="px-3">
                {t('sales.statement.scopeAll')}
              </ToggleGroupItem>
            </ToggleGroup>
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={downloading}>
            {t('common.cancel')}
          </Button>
          <Button onClick={handleDownload} disabled={!canSubmit}>
            {downloading ? <Loader2 className="size-4 animate-spin" /> : <FileDown className="size-4" />}
            {t('sales.statement.download')}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
